import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, MapPin, Clock, X } from 'lucide-react';

const DeliveryCalendar = ({ orders }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  const daysInMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0
  ).getDate();

  const firstDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  ).getDay();

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const getDeliveriesForDate = (date) => {
    return orders.filter(order => {
      const deliveryDate = new Date(order.deliveryDate);
      return deliveryDate.getDate() === date &&
             deliveryDate.getMonth() === currentDate.getMonth() &&
             deliveryDate.getFullYear() === currentDate.getFullYear();
    });
  };

  // Format time to 12-hour format
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  // Format address for display
  const formatAddress = (order) => {
    return `${order.streetAddress}${order.apartmentUnit ? ', ' + order.apartmentUnit : ''}, ${order.city}, FL ${order.zipCode}`;
  };

  return (
    <div className="bg-amber-50 rounded-lg shadow-lg p-6">
      {/* Calendar Header */}
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={previousMonth}
          className="p-2 hover:bg-rose-100 rounded-full"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        
        <h2 className="text-xl font-bold text-rose-800">
          {months[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h2>
        
        <button
          onClick={nextMonth}
          className="p-2 hover:bg-rose-100 rounded-full"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-2">
        {/* Day names */}
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="text-center font-semibold text-gray-600 p-2">
            {day}
          </div>
        ))}

        {/* Empty cells for days before start of month */}
        {Array.from({ length: firstDayOfMonth }).map((_, index) => (
          <div key={`empty-${index}`} className="p-2" />
        ))}

        {/* Calendar days */}
        {Array.from({ length: daysInMonth }).map((_, index) => {
          const date = index + 1;
          const deliveries = getDeliveriesForDate(date);
          const isToday = new Date().getDate() === date &&
                         new Date().getMonth() === currentDate.getMonth() &&
                         new Date().getFullYear() === currentDate.getFullYear();

          return (
            <div
              key={date}
              className={`min-h-32 p-2 border rounded-lg ${
                isToday ? 'bg-rose-50 border-rose-300' : 'border-gray-200'
              }`}
            >
              <div className="font-semibold text-gray-700 mb-1">{date}</div>
              
              <div className="space-y-1">
                {deliveries.map(delivery => (
                  <button
                    key={delivery._id}
                    onClick={() => setSelectedOrder(delivery)}
                    className="w-full text-left text-xs bg-rose-100 hover:bg-rose-200 text-rose-800 p-2 rounded transition-colors"
                  >
                    <div className="font-semibold">{delivery.customerName}</div>
                    <div className="flex items-center gap-1 text-rose-600">
                      <Clock className="w-3 h-3" />
                      {formatTime(delivery.deliveryDate)}
                    </div>
                    <div className="flex items-center gap-1 text-rose-600 truncate">
                      <MapPin className="w-3 h-3 flex-shrink-0" />
                      {formatAddress(delivery)}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-rose-800">Delivery Details</h3>
              <button 
                onClick={() => setSelectedOrder(null)}
                className="p-2 hover:bg-rose-100 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="font-semibold text-gray-600">Customer</label>
                <p className="text-gray-800">{selectedOrder.customerName}</p>
              </div>

              <div>
                <label className="font-semibold text-gray-600">Delivery Time</label>
                <p className="text-gray-800">
                  {new Date(selectedOrder.deliveryDate).toLocaleDateString()} at{' '}
                  {formatTime(selectedOrder.deliveryDate)}
                </p>
              </div>

              <div>
                <label className="font-semibold text-gray-600">Contact</label>
                <p className="text-gray-800">{selectedOrder.customerEmail}</p>
              </div>

              <div>
                <label className="font-semibold text-gray-600">Delivery Address</label>
                <div className="bg-rose-50 p-3 rounded-lg mt-1">
                  <p>{selectedOrder.streetAddress}</p>
                  {selectedOrder.apartmentUnit && <p>{selectedOrder.apartmentUnit}</p>}
                  <p>{selectedOrder.city}, FL {selectedOrder.zipCode}</p>
                </div>
              </div>

              <div>
                <label className="font-semibold text-gray-600">Order Items</label>
                <div className="mt-1 space-y-1">
                  {selectedOrder.items.map((item, index) => (
                    <div key={index} className="flex justify-between bg-rose-50 p-2 rounded-lg">
                      <span>{item.name} × {item.quantity}</span>
                      <span className="font-semibold">${item.price}</span>
                    </div>
                  ))}
                </div>
              </div>

              {selectedOrder.specialNotes && (
                <div>
                  <label className="font-semibold text-gray-600">Special Notes</label>
                  <p className="text-gray-800 bg-rose-50 p-3 rounded-lg mt-1">
                    {selectedOrder.specialNotes}
                  </p>
                </div>
              )}

              <div className="pt-4 border-t">
                <label className="font-semibold text-gray-600">Status</label>
                <div className="mt-2">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium
                    ${selectedOrder.status === 'Completed' ? 'bg-green-100 text-green-800' :
                      selectedOrder.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                      selectedOrder.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'}`}>
                    {selectedOrder.status}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryCalendar;